import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Compass,
  Users2,
  Rocket,
  LayoutDashboard,
  BookMarked,
  ArrowUpRight,
} from "lucide-react";
import PageHero from "../components/PageHero";
import HowWeWork from "../components/sections/HowWeWork";
import { SOLUTIONS } from "../lib/solutions";
import { CALENDLY_URL } from "../lib/site";

const ICONS = { Compass, Users2, Rocket, LayoutDashboard, BookMarked };

export default function SolutionsIndex() {
  return (
    <div data-testid="solutions-index-page">
      <PageHero
        eyebrow="Our Solutions"
        accent="#FF007F"
        title={
          <>
            Practical solutions for{" "}
            <span className="bg-[#FFD700] px-2 inline-block">
              future-ready
            </span>{" "}
            learning.
          </>
        }
        intro="Five end-to-end offerings for schools, libraries and councils — from strategy and consulting to launch, educator PD, resources and the TinkerShift Platform."
        ctaLabel="Book a Discovery Call"
        ctaTo={CALENDLY_URL}
        ctaExternal
        secondaryLabel="See how we work"
        secondaryTo="/#how-we-work"
      />

      <section className="bg-white border-b-2 border-[#1A1A1A]">
        <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-7">
            {SOLUTIONS.map((s, i) => {
              const Icon = ICONS[s.icon] || Compass;
              return (
                <motion.article
                  key={s.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: i * 0.05 }}
                  style={{ backgroundColor: s.color, color: s.text }}
                  data-testid={`solutions-index-card-${s.slug}`}
                  className="rounded-3xl border-2 border-[#1A1A1A] p-8 md:p-10 nb-shadow nb-lift relative overflow-hidden"
                >
                  <div className="flex items-start justify-between">
                    <span className="h-14 w-14 rounded-2xl bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] flex items-center justify-center nb-shadow-sm">
                      <Icon size={26} strokeWidth={2.5} />
                    </span>
                    <span className="font-display font-black text-6xl leading-none opacity-15 tracking-tighter">
                      {s.number}
                    </span>
                  </div>
                  <p className="mt-6 font-mono text-[11px] uppercase tracking-widest opacity-80">
                    Solution {s.number} · {s.accent}
                  </p>
                  <h2 className="mt-1 font-display font-black text-3xl md:text-4xl leading-tight tracking-tighter">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-base md:text-lg font-semibold opacity-95 leading-relaxed">
                    {s.subheading}
                  </p>
                  <p className="mt-6 font-mono text-sm uppercase tracking-widest opacity-80">
                    From AUD ${s.investment.from.toLocaleString("en-AU")}
                    {s.investment.per ? ` / ${s.investment.per}` : ""}
                  </p>
                  <Link
                    to={`/solutions/${s.slug}`}
                    data-testid={`solutions-index-cta-${s.slug}`}
                    className={`mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 font-bold uppercase text-xs tracking-widest border-2 nb-shadow-sm nb-press ${
                      s.text === "#FFFFFF"
                        ? "bg-white text-[#1A1A1A] border-[#1A1A1A]"
                        : "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                    }`}
                  >
                    {s.cta} <ArrowUpRight size={14} strokeWidth={2.5} />
                  </Link>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      <div id="how-we-work" />
      <HowWeWork />
    </div>
  );
}
